import { Add } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import { ClearIcon } from '@mui/x-date-pickers';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getSupplierListApi } from '../../redux/Supplier/action';
import { truncateString } from '../../utility/helpers/commonFunction';
import { setItem } from '../../utility/localStorage';
import { formatPhoneNumber } from '../Common/FormatPhoneNumber';

const Suppliers = () => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [suppliers, setSuppliers] = useState([]);
    const [showLoader, setShowLoader] = useState(true);
    const [fullPageLoader, setFullPageLoader] = useState(false);

    // Pagination and Sorting
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [orderBy, setOrderBy] = useState('supplierName');
    const [order, setOrder] = useState('asc');

    // Search and Filter
    const [searchBy, setSearchBy] = useState('name'); // Default to "name"
    const [searchValue, setSearchValue] = useState('');
    const [selectedSearchType, setSelectedSearchType] = useState('Supplier Name');

    const cancelTokenSourceRef = useRef(null);

    const getParams = () => ({
        pagination: true,
        page: page + 1,
        limit: rowsPerPage,
        searchValue: searchValue.trim(),
        searchBy,
        sortBy: orderBy,
        sortType: order.toUpperCase(),
    });

    const fetchSupplierList = async () => {
        setShowLoader(true);

        // Cancel previous API call if ongoing
        if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel('Canceled due to new request.');
        }

        cancelTokenSourceRef.current = axios.CancelToken.source();

        try {
            const response = await dispatch(
                getSupplierListApi(getParams(), {
                    cancelToken: cancelTokenSourceRef.current.token,
                })
            );
            if (response?.data?.length > 0) {
                setSuppliers(response || []);
            } else {
                setSuppliers([]);
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request canceled:', error.message);
            } else {
                console.error('Error fetching suppliers:', error);
            }
        } finally {
            setShowLoader(false);
        }
    };

    const handleSearchChange = (event) => {
        setPage(0);
        setSearchValue(event.target.value);
    };

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectSearchType = (type) => {
        setSearchBy(type);
        setSelectedSearchType(
            {
                name: 'Supplier Name',
                phoneNumber: 'Phone Number',
                email: 'Email',
                contactPerson: 'Contact Person',
            }[type] || 'Supplier Name'
        );
        setSearchValue('');
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => fetchSupplierList(), 800);
        return () => clearTimeout(delayDebounceFn);
    }, [searchValue, orderBy, order, page, rowsPerPage]);

    useEffect(() => {
        fetchSupplierList();
    }, []);

    const TableRowsLoader = ({ rowsNum }) => {
        return [...Array(rowsNum)].map((row, index) => (
            <TableRow key={index}>
                <TableCell component="th" scope="row">
                    <Skeleton animation="wave" variant="text" />
                </TableCell>
                <TableCell>
                    <Skeleton animation="wave" variant="text" />
                </TableCell>
                <TableCell>
                    <Skeleton animation="wave" variant="text" />
                </TableCell>
                <TableCell>
                    <Skeleton animation="wave" variant="text" />
                </TableCell>
                <TableCell>
                    <Skeleton animation="wave" variant="text" />
                </TableCell>
                <TableCell>
                    <Skeleton animation="wave" variant="text" />
                </TableCell>
            </TableRow>
        ));
    };

    return (
        <>
            <Box padding={'32px 24px'} sx={{ bgcolor: 'background.main' }}>
                <Grid container rowGap={2}>
                    <Grid container item justifyContent={'space-between'} padding={'16px 0px'} spacing={2}>
                        <Grid item lg={5}>
                            <Typography variant="h4" sx={{ fontWeight: 400 }}>
                                Suppliers{' '}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Stack direction={'row'} gap={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    // startIcon={<Add />}
                                    onClick={() => {
                                        navigate('/suppliers-invoice/all');
                                    }}
                                    size="large"
                                >
                                    View Invoices
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        navigate('/suppliers/new');
                                    }}
                                    size="large"
                                >
                                    NEW SUPPLIER
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        navigate('/suppliers-invoice/new');
                                    }}
                                    size="large"
                                >
                                    NEW SUPPLIER INVOICE
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        bgcolor: 'white',
                        mt: '59px',
                        p: '24px',
                    }}
                    boxShadow={2}
                >
                    <Grid container columns={13} item alignItems={'center'} spacing={2}>
                        <Grid item xs={13} sm={5} md={4} lg={2.5}>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Search by</InputLabel>
                                <Select
                                    fullWidth
                                    label="Search by"
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={searchBy}
                                    onChange={(e) => handleSelectSearchType(e.target.value)}
                                >
                                    <MenuItem value="name" key="name">
                                        Supplier Name
                                    </MenuItem>
                                    <MenuItem value="contactPerson" key="contactPerson">
                                        Contact Person
                                    </MenuItem>
                                    <MenuItem value="phoneNumber" key="phoneNumber">
                                        Phone Number
                                    </MenuItem>
                                    <MenuItem value="email" key="email">
                                        Email
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={13} sm={8} md={9} lg={10.5}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="outlined-adornment-search" style={{ backgroundColor: 'transparent', color: 'black' }}>
                                    Search
                                </InputLabel>
                                <OutlinedInput
                                    style={{ width: '100%' }}
                                    fullWidth
                                    id="outlined-adornment-search"
                                    sx={{ backgroundColor: 'transparent', color: 'black' }}
                                    placeholder={searchBy ? selectedSearchType : ' Supplier name, phone number, email, etc...'}
                                    onChange={handleSearchChange}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    }
                                    endAdornment={
                                        searchValue && (
                                            <InputAdornment
                                                position="end"
                                                className="search-close-icon"
                                                onClick={() => {
                                                    setSearchValue('');
                                                }}
                                            >
                                                <ClearIcon />
                                            </InputAdornment>
                                        )
                                    }
                                    label="Search"
                                    value={searchValue}
                                    disabled={searchBy ? false : true}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Divider
                        style={{
                            marginTop: '20px',
                            marginBottom: '20px',
                            width: '100%',
                            height: '10px',
                        }}
                    />
                    <Grid container item borderRadius={'10px'}>
                        {console.log('Sdfsdfds', suppliers?.data?.length, showLoader)}
                        {(suppliers?.data?.length === 0 || !suppliers?.data) && !showLoader ? (
                            <div className="not-found-class">Sorry, we could not find any matches.</div>
                        ) : (
                            <Grid item xl={12} md={12} sm={12} xs={12}>
                                <TableContainer
                                    sx={{
                                        width: '100%',
                                    }}
                                >
                                    <Table stickyHeader padding="16px" sx={{ tableLayout: 'fixed' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: '186px' }}>
                                                    <TableSortLabel
                                                        active={orderBy === 'supplierName'}
                                                        direction={orderBy === 'supplierName' ? order : 'asc'}
                                                        onClick={() => handleSortRequest('supplierName')}
                                                    >
                                                        Supplier Name
                                                    </TableSortLabel>{' '}
                                                </TableCell>
                                                <TableCell style={{ width: '186px' }}>Account #</TableCell>
                                                <TableCell style={{ width: '186px' }}>
                                                    <TableSortLabel
                                                        active={orderBy === 'fullName'}
                                                        direction={orderBy === 'fullName' ? order : 'asc'}
                                                        onClick={() => handleSortRequest('fullName')}
                                                    >
                                                        Contact Name
                                                    </TableSortLabel>{' '}
                                                </TableCell>
                                                <TableCell style={{ width: '186px' }}>Phone Number</TableCell>
                                                <TableCell style={{ width: '186px' }}>Email</TableCell>

                                                <TableCell style={{ width: '186px' }}>Website</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {showLoader ? (
                                                <TableRowsLoader rowsNum={rowsPerPage} />
                                            ) : (
                                                Array.isArray(suppliers?.data) &&
                                                suppliers.data.map((data) => (
                                                    <TableRow
                                                        key={data._id}
                                                        onClick={() => {
                                                            setItem('supplierProfileTabs', 0);
                                                            navigate(`${data?._id}`);
                                                        }}
                                                        hover={true}
                                                        style={{ cursor: 'pointer' }}
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 108, 221, 0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell component="th" scope="row">
                                                            {data?.name}
                                                        </TableCell>
                                                        <TableCell align="left">{data?.account}</TableCell>
                                                        <TableCell align="left">
                                                            {data?.contactPerson?.firstName || data?.contactPerson?.lastName
                                                                ? `${data?.contactPerson?.firstName || ''} ${data?.contactPerson?.lastName || ''}`.trim()
                                                                : ''}
                                                        </TableCell>

                                                        <TableCell align="left">{data?.phoneNumber ? formatPhoneNumber(data?.phoneNumber) : ''}</TableCell>
                                                        <TableCell align="left">{truncateString(data?.primaryEmail, 25)}</TableCell>

                                                        <TableCell align="left">
                                                            {data?.website && data?.website !== '' ? truncateString(data?.website, 25) : '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Grid container item alignItems={'center'} justifyContent="end">
                                    <Grid item>
                                        {suppliers?.count > 0 && (
                                            <TablePagination
                                                component="div"
                                                count={suppliers?.count}
                                                page={page}
                                                onPageChange={handleChangePage}
                                                rowsPerPageOptions={[10, 20, 50, 100]}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>

            <Dialog fullScreen={false} open={fullPageLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Suppliers;
